import React from 'react'

import classNames from 'clsx'
import memoizeOne from 'memoize-one'
import PropTypes from 'prop-types'

import { Tooltip } from '@mui/material'
import { makeStyles } from '@mui/styles'

import { regexPropType } from '~/src/Lib/PropTypes'
import { EMPTY_OBJECT } from '~/src/Lib/Utils'

const useStyles = makeStyles(theme => ({
  root: ({ maxWidth }) => (maxWidth != null ? ({ maxWidth }) : EMPTY_OBJECT),
  default: {
    background: theme.palette.primary.main,
    '& [class*=MuiTooltip-arrow]': {
      color: theme.palette.primary.main,
    },
  },
  dark: {
    background: theme.palette.background.dark,
    '& [class*=MuiTooltip-arrow]': {
      color: theme.palette.background.dark,
    },
  },
  main: {
    background: theme.palette.background.default,
    '& [class*=MuiTooltip-arrow]': {
      color: theme.palette.background.default,
    },
  },
  light: {
    background: theme.palette.background.secondary,
    '& [class*=MuiTooltip-arrow]': {
      color: theme.palette.background.secondary,
    },
  },
  paper: {
    background: theme.palette.background.paper,
    '& [class*=MuiTooltip-arrow]': {
      color: theme.palette.background.paper,
    },
  },
  arrow: {},
  tooltip: {},
}), { name: 'Aroya/Tooltip' })

const getClasses = memoizeOne((classes, variant) => ({
  tooltip: classNames(classes.root, classes[variant] ?? classes.default)
}))

/**
 * A tooltip that is easily styled with Aroya background colors
 * @component
 * @alias Tooltip
 * @composes
 * from: '@mui/material/Tooltip'
 * link: https://material-ui.com/api/tooltip/#props
 * @example
 * <Box
 *   display="grid"
 *   gap="1rem"
 *   gridTemplateColumns="repeat(auto-fill, minmax(7rem, 1fr))"
 *   padding="4rem"
 *   bgcolor="rgba(255, 255, 255, 0.32)"
 * >
 *   {[
 *     [null, undefined],
 *     ['dark', 'top'],
 *     ['light', 'right'],
 *     ['main', 'bottom'],
 *     ['paper', 'left']
 *   ].map(([variant, placement], i) => (
 *     <Tooltip
 *       arrow
 *       placement={placement}
 *       title={(
 *         <Box component={T.Tiny} color="text.primary" padding="0.5rem">
 *           {String(variant)}
 *         </Box>
 *       )}
 *       variant={variant}
 *     >
 *       <T>tooltip: {String(variant)}</T>
 *     </Tooltip>
 *   ))}
 * </Box>
 */
const TooltipComponent = ({
  children,
  variant = null,
  maxWidth = null,
  ...passthru
}) => {
  const classes = useStyles({ maxWidth })
  return (
    <Tooltip
      {...passthru}
      classes={getClasses(classes, variant)}
    >
      {children || <span />}
    </Tooltip>
  )
}
TooltipComponent.displayName = 'Tooltip'

TooltipComponent.propTypes = {
  ...Tooltip.propTypes,
  maxWidth: PropTypes.oneOfType([
    PropTypes.oneOf(['none', 'max-content', 'min-content']),
    PropTypes.number,
    regexPropType(/\d+(cm|mm|in|pc|pt|px|r?em|ex|ch|vh|vmin|vmax|vw|%)/, true)
  ]),
  variant: PropTypes.oneOf([null, 'dark', 'light', 'main', 'paper']),
  zIndex: PropTypes.number,
}

export default TooltipComponent
